import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import type { ISeriesApi } from 'lightweight-charts';
import type { CandleData, Timeframe } from '../types';
import { getMarketSession } from '../utils/tradingHours';
import type { LastPriceCountdownOverlayHandle } from './LastPriceCountdownOverlay';

export interface LastPriceCountdownOverlayControllerHandle {
  onTick: (debugSourcePerfTs?: number) => void;
  refresh: (debugSourcePerfTs?: number) => void;
}

export interface LastPriceCountdownOverlayControllerProps {
  overlayRef: React.RefObject<LastPriceCountdownOverlayHandle | null>;
  getSeries: () => ISeriesApi<'Candlestick'> | null;
  getCandles: () => CandleData[];
  getTimeframe: () => Timeframe;
  replayMode?: 'off' | 'selecting' | 'active';
}

const formatRemainingSeconds = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const pad = (n: number) => String(n).padStart(2, '0');

  if (s < 3600) {
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${pad(mm)}:${pad(ss)}`;
  }

  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
};

const getTimeframeSeconds = (tf: Timeframe): number => {
  switch (tf) {
    case '1m': return 60;
    case '5m': return 300;
    case '15m': return 900;
    case '30m': return 1800;
    case '1h': return 3600;
    case '4h': return 14400;
    case '1d': return 86400;
    case '1w': return 604800;
    default: return 60;
  }
};

const getRemainingSecondsInTimeframeBucket = (tfSeconds: number, now: Date): number => {
  const secondsSinceMidnight =
    now.getHours() * 3600 +
    now.getMinutes() * 60 +
    now.getSeconds() +
    now.getMilliseconds() / 1000;

  if (tfSeconds <= 0) return 0;

  // Weekly: align to Monday 00:00 in the given timezone
  if (tfSeconds >= 604800) {
    const dayOfWeek = now.getDay(); // 0=Sun..6=Sat
    const mondayBased = (dayOfWeek + 6) % 7; // 0=Mon..6=Sun
    const secondsIntoWeek = mondayBased * 86400 + secondsSinceMidnight;
    const remainder = secondsIntoWeek % 604800;
    return Math.max(0, 604800 - remainder);
  }

  // Daily: align to local midnight
  if (tfSeconds >= 86400) {
    return Math.max(0, 86400 - secondsSinceMidnight);
  }

  // Intraday: align to local midnight
  const remainder = secondsSinceMidnight % tfSeconds;
  return Math.max(0, tfSeconds - remainder);
};

export const LastPriceCountdownOverlayController = forwardRef<LastPriceCountdownOverlayControllerHandle, LastPriceCountdownOverlayControllerProps>(({
  overlayRef,
  getSeries,
  getCandles,
  getTimeframe,
  replayMode = 'off',
}, ref) => {
  const rafIdRef = useRef<number | null>(null);
  const rafPendingRef = useRef(false);
  const needsRunRef = useRef(false);
  const lastKnownYRef = useRef<number>(24);
  const debugSourcePerfTsRef = useRef<number | undefined>(undefined);
  const lastDebugLogPerfRef = useRef<number>(0);
  const lastKnownCandleRef = useRef<CandleData | null>(null);

  const updateNow = useCallback(() => {
    const series = getSeries();
    const candles = getCandles();
    const debugEnabled = typeof window !== 'undefined' && Boolean((window as any).__overlayLatencyDebug);
    if (debugEnabled && typeof performance !== 'undefined') {
      const now = performance.now();
      if (now - lastDebugLogPerfRef.current >= 1000) {
        lastDebugLogPerfRef.current = now;
        console.log('[LastPriceOverlay] controller.updateNow', {
          candles: candles.length,
          hasSeries: !!series,
          hasOverlay: !!overlayRef.current,
          lastKnownY: lastKnownYRef.current,
        });
      }
    }

    const overlay = overlayRef.current;
    if (!overlay) return;

    const lastFromSource = candles.length > 0 ? candles[candles.length - 1] : null;
    const lastFromCache = lastKnownCandleRef.current;
    const isValidCandle = (c: CandleData | null): c is CandleData => {
      if (!c) return false;
      if (!Number.isFinite(c.time)) return false;
      if (!Number.isFinite(c.open) || !Number.isFinite(c.high) || !Number.isFinite(c.low) || !Number.isFinite(c.close)) return false;
      if (c.open <= 0 || c.high <= 0 || c.low <= 0 || c.close <= 0) return false;
      return true;
    };

    const last = isValidCandle(lastFromSource) ? lastFromSource : lastFromCache;
    if (!isValidCandle(last)) {
      overlay.update({
        visible: false,
        y: 0,
        price: 0,
        isUp: true,
        countdownText: '',
      });
      return;
    }

    if (isValidCandle(lastFromSource)) {
      lastKnownCandleRef.current = lastFromSource;
    }

    const tfSeconds = getTimeframeSeconds(getTimeframe());
    const estNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const remainingSeconds = getRemainingSecondsInTimeframeBucket(tfSeconds, estNow);

    const isCountdownRelevant = Number.isFinite(remainingSeconds) && remainingSeconds > 0 && remainingSeconds <= tfSeconds;
    const isMarketClosed = getMarketSession() === 'closed';
    const isReplayActive = replayMode === 'active' || replayMode === 'selecting';

    const y = series ? series.priceToCoordinate(last.close as any) : null;
    const resolvedY = y == null ? lastKnownYRef.current : y;
    lastKnownYRef.current = resolvedY;

    overlay.update({
      visible: true,
      y: resolvedY,
      price: last.close,
      isUp: last.close >= last.open,
      countdownText: !isMarketClosed && !isReplayActive && isCountdownRelevant ? formatRemainingSeconds(remainingSeconds) : '',
      debugSourcePerfTs: debugSourcePerfTsRef.current,
    });
  }, [getCandles, getSeries, getTimeframe, overlayRef, replayMode]);

  const schedule = useCallback((useDoubleRaf: boolean) => {
    if (rafPendingRef.current) {
      needsRunRef.current = true;
      return;
    }
    rafPendingRef.current = true;

    const run = () => {
      rafPendingRef.current = false;
      updateNow();

      if (needsRunRef.current) {
        needsRunRef.current = false;
        schedule(false);
      }
    };

    rafIdRef.current = window.requestAnimationFrame(() => {
      if (useDoubleRaf) {
        rafIdRef.current = window.requestAnimationFrame(run);
      } else {
        run();
      }
    });
  }, [updateNow]);

  useImperativeHandle(ref, () => ({
    onTick: (debugSourcePerfTs?: number) => {
      debugSourcePerfTsRef.current = debugSourcePerfTs ?? (typeof performance !== 'undefined' ? performance.now() : undefined);
      schedule(true);
    },
    refresh: (debugSourcePerfTs?: number) => {
      debugSourcePerfTsRef.current = debugSourcePerfTs ?? (typeof performance !== 'undefined' ? performance.now() : undefined);
      schedule(true);
    },
  }), [schedule]);

  useEffect(() => {
    debugSourcePerfTsRef.current = typeof performance !== 'undefined' ? performance.now() : undefined;
    schedule(true);
    const id = window.setInterval(() => updateNow(), 1000);
    return () => window.clearInterval(id);
  }, [schedule, updateNow]);

  useEffect(() => {
    return () => {
      if (rafIdRef.current != null) {
        window.cancelAnimationFrame(rafIdRef.current);
      }
      rafPendingRef.current = false;
      needsRunRef.current = false;
    };
  }, []);

  return null;
});

LastPriceCountdownOverlayController.displayName = 'LastPriceCountdownOverlayController';
